'use client';
/**
 * @file     ThemeStateProvider.tsx
 * @readme   ./README.md
 * @purpose  Lightweight theme state provider, manages mode, localStorage, system preference sync, and exposes the design theme via ThemeContext. NO MUI or Emotion imports, this is the critical-path provider that hydrates immediately.
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import type { AppTheme, ThemeMode, ThemePreference } from '@/core/types';
import { getLocalStorage } from '@/core/utils/browserRuntime';
import { createDesignTheme } from '@/design-system/theme';
import { ThemeContext } from './ThemeContext';
import { env } from '@/core/config/env';
import {
  applyDocumentThemeState,
  COLOR_SCHEME_QUERY,
  getBootThemeState,
  getSystemThemeMode,
  resolveThemeMode,
  writeStoredThemePreference,
} from './webThemeState';

const DEFAULT_FALLBACK_MODE: ThemeMode = 'light';

type ThemeStateProviderProps = {
  children: React.ReactNode;
  initialMode?: ThemeMode;
  overrides?: Partial<Omit<AppTheme, 'mode'>>;
};

type ThemeState = {
  mode: ThemeMode;
  preference: ThemePreference;
};

export const ThemeStateProvider = ({
  children,
  initialMode,
  overrides,
}: ThemeStateProviderProps) => {
  const fallbackMode = initialMode ?? (env.defaultThemeMode as ThemeMode) ?? DEFAULT_FALLBACK_MODE;
  const [themeState, setThemeState] = useState<ThemeState>({
    mode: fallbackMode,
    preference: fallbackMode as ThemePreference,
  });

  const applyThemeState = useCallback(
    (nextPreference: ThemePreference, nextMode?: ThemeMode) => {
      const storage = getLocalStorage();
      const systemMode = getSystemThemeMode(
        typeof window !== 'undefined' && typeof window.matchMedia === 'function'
          ? window.matchMedia(COLOR_SCHEME_QUERY).matches
          : null,
      );
      const resolvedMode =
        nextMode ?? resolveThemeMode({ preference: nextPreference, systemMode, fallbackMode });

      writeStoredThemePreference(storage, nextPreference);
      applyDocumentThemeState(
        typeof document === 'undefined' ? null : document,
        { mode: resolvedMode, preference: nextPreference, ready: true },
      );
      setThemeState({ mode: resolvedMode, preference: nextPreference });
    },
    [fallbackMode],
  );

  // Sync from boot script on mount
  useLayoutEffect(() => {
    const boot = getBootThemeState(fallbackMode);
    applyDocumentThemeState(document, { mode: boot.mode, preference: boot.preference, ready: true });
    setThemeState({ mode: boot.mode, preference: boot.preference });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // System preference listener
  useEffect(() => {
    if (themeState.preference !== 'system') return;
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

    const mediaQuery = window.matchMedia(COLOR_SCHEME_QUERY);
    const handleChange = (event?: MediaQueryListEvent) => {
      const nextMode = getSystemThemeMode(event ? event.matches : mediaQuery.matches);
      if (!nextMode) return;
      applyDocumentThemeState(
        typeof document === 'undefined' ? null : document,
        { mode: nextMode, preference: 'system', ready: true },
      );
      setThemeState((current) => ({ ...current, mode: nextMode }));
    };

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, [themeState.preference]);

  const theme = useMemo(
    () => createDesignTheme(themeState.mode, overrides),
    [overrides, themeState.mode],
  );

  const contextValue = useMemo(
    () => ({
      mode: themeState.mode,
      setMode: (mode: ThemeMode) => applyThemeState(mode, mode),
      toggleMode: () => applyThemeState(themeState.mode === 'light' ? 'dark' : 'light'),
      theme,
    }),
    [applyThemeState, theme, themeState.mode],
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};
